import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, UploadCloud, FileText, ArrowLeft, Check, CheckCircle2, Sliders, Sparkles, Loader2, RotateCcw } from "lucide-react";
import { Button, Spinner } from "./ui/kit";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Vyber from "./Vyber";
import PrehledImportu from "./PrehledImportu";

const FIELDS = [
  ["nazev", "Název *"], ["katalogove", "Kat. číslo"], ["ean", "EAN"],
  ["mnozstvi", "Množství"], ["mj", "MJ"], ["cena_nakup", "Cena *"],
  ["cena_prodej", "Prodejní cena"], ["sleva_pct", "Sleva %"], ["dph", "DPH %"],
  ["cena_prace", "Montáž/ks"], ["typ", "Typ"],
];
const TYP_LABEL = { MATERIAL: "Materiál", PRACE: "Práce", OSTATNI: "Ostatní" };
const DRUH = { A: "Hlavní zařízení", B: "Příslušenství", C: "Drobný materiál" };
const NOMULTI = { headers: { "Content-Type": undefined } };
const ETAPA_RIM = ["—", "I", "II", "III"];
// Co s cenou položky, která je v ceníku s jinou cenou. Výchozí: jen tato nabídka.
const VOLBY = [["NABIDKA", "Jen pro tuto nabídku"], ["CENIK_OPRAVIT", "Opravit i ceník"], ["CENIK_POUZIT", "Použít cenu z ceníku"]];
const VOLBA_POPIS = {
  NABIDKA: "Nabídka dostane cenu ze souboru, ceník zůstane.",
  CENIK_OPRAVIT: "Nabídka i ceník dostanou cenu ze souboru.",
  CENIK_POUZIT: "Nabídka dostane cenu z ceníku, cena ze souboru se nepoužije.",
};
const parseKc = (t) => Number(String(t || "").replace(/\s/g, "").replace(",", ".")) || 0;
const cislo = (t) => { const n = parseFloat(String(t ?? "").replace(/\s/g, "").replace(",", ".")); return Number.isFinite(n) ? n : null; };
const pct = (n) => `${String(Math.round(n * 10) / 10).replace(".", ",")} %`;
const cas = (d) => new Date(d).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });

// Přepínač po řádku: na telefonu tři stejné sloupce s textem na dva řádky, na počítači pilulky.
const SEG = "min-h-[40px] md:min-h-0 md:h-7 px-2 md:px-2.5 rounded-pill t-meta font-600 leading-tight md:whitespace-nowrap";
const SEG_SKUPINA = "grid grid-cols-3 md:inline-flex gap-0.5 p-0.5 rounded-[14px] md:rounded-pill bg-line-soft md:self-start";
const segCls = (on) => `${SEG} ${on ? "bg-ink text-white" : "text-sec hover:text-ink"}`;
const FILTR = "tap md:min-h-0 shrink-0 inline-flex items-center gap-1.5 h-9 md:h-8 px-3 rounded-pill border t-meta font-600";
const CHIP = "h-8 px-3 rounded-pill border border-[#3a3e44] text-white t-meta font-700 hover:bg-sidebar-3";
const STITEK = "inline-flex items-center h-5 px-2 rounded-pill text-[9.5px] tracking-[0.07em] uppercase font-700 whitespace-nowrap";

// bezCeniku = subdodavatel: do ceníku hostitele nezapisuje a jeho ceny nevidí
// (server to vynucuje, tady jen nenabízíme, co nejde). dph = sazba nabídky.
export default function ImportWizard({ vid, onClose, onDone, bezCeniku = false, dph = null, nabidka = null, onNazev }) {
  const { company } = useAuth();
  const [step, setStep] = useState(1);          // 1 upload | 2 kontrola | 3 výsledek
  const [file, setFile] = useState(null);
  const [soubor, setSoubor] = useState("");     // název souboru (i z obnoveného konceptu)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("excel");    // excel | ai
  const [analyza, setAnalyza] = useState(null);
  const [mapping, setMapping] = useState({});
  const [sheet, setSheet] = useState(null);
  const [rows, setRows] = useState([]);
  const [showMapping, setShowMapping] = useState(false);
  const [aiPhase, setAiPhase] = useState(null);  // null | 'reading'
  const [puvod, setPuvod] = useState(null);      // {dodavatel, ico, jistota} z AI
  const [stats, setStats] = useState(null);
  // Kontrola proti ceníku: {i: {match, polozka, rozdil_kc, rozdil_pct, podobna, vyrobce_navrh}}.
  const [par, setPar] = useState({});
  const [parNacteno, setParNacteno] = useState(false);   // bez párování by se nové položky nezaložily
  const [rowsVerze, setRowsVerze] = useState(0);
  // Rozhodnutí po řádcích (klíč r._i). Marže je text — prázdné pole zůstane prázdné.
  const [marze, setMarze] = useState({});
  // Prodejní import: nákup napsaný k řádku (soubor ho nemá) — jde i do ceníku.
  const [nakupy, setNakupy] = useState({});
  const [zdroj, setZdroj] = useState({});
  const [volba, setVolba] = useState({});
  const [doCeniku, setDoCeniku] = useState({});
  // Prodejní import: shodu jen podle názvu potvrdí uživatel („je to ona“) → i → id položky ceníku.
  const [potvrzena, setPotvrzena] = useState({});
  const [vyrobce, setVyrobce] = useState({});
  const [druh, setDruh] = useState({});
  const [vybrane, setVybrane] = useState({});
  const [vybirani, setVybirani] = useState(false);   // telefon: zaškrtávací políčka jen na požádání
  const [filtr, setFiltr] = useState("vse");
  const [vyrobci, setVyrobci] = useState([]);
  // NAKUP = ceny od dodavatele (prodej = nákup + marže), PRODEJ = moje nabídka klientovi
  // (prodej přesně podle souboru, ceník se nemění).
  const [ceny, setCeny] = useState("NAKUP");
  const [sleva, setSleva] = useState("");
  const [koncept, setKoncept] = useState(null);      // uložený koncept nabízený k obnovení
  const [ulozeno, setUlozeno] = useState(null);      // čas posledního uložení konceptu
  const [vysledek, setVysledek] = useState(null);
  const [souhrnImportu, setSouhrnImportu] = useState(null);
  const [vraceno, setVraceno] = useState(null);
  const [nazev, setNazev] = useState(nabidka?.nazev || "");    // název nabídky v kroku 3
  const nakupni = ceny === "NAKUP" && !bezCeniku;
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const ulozTimer = useRef(null);

  useEffect(() => () => { clearInterval(pollRef.current); clearTimeout(ulozTimer.current); }, []);

  useEffect(() => {
    api.get(`/api/nabidky-verze/${vid}/import/koncept`)
      .then(({ data }) => { if (data.koncept?.rows?.length) setKoncept(data); })
      .catch(() => {});
    if (!bezCeniku) api.get("/api/vyrobci").then(({ data }) => setVyrobci(data.items || data || [])).catch(() => setVyrobci([]));
  }, [vid, bezCeniku]);

  const vynulujRozhodnuti = () => {
    setMarze({}); setNakupy({}); setZdroj({}); setVolba({}); setDoCeniku({}); setVyrobce({}); setDruh({}); setPotvrzena({});
    setVybrane({}); setFiltr("vse");
  };
  const setFromRows = (radky, { md, st = null } = {}) => {
    setRows((radky || []).map((r, i) => ({ ...r, _i: i })));
    vynulujRozhodnuti(); setMode(md); setStats(st); setAiPhase(null); setStep(2); setRowsVerze((v) => v + 1);
  };

  // Po načtení řádků se zjistí, co už v ceníku je, jestli sedí cena a čí je.
  useEffect(() => {
    if (step !== 2 || !rows.length) { setPar({}); return undefined; }
    let zruseno = false;
    setParNacteno(false);
    // Prodejní ceny se párují přísněji: nákup z ceníku jen při shodě katalogového čísla / EAN.
    api.post(`/api/nabidky-verze/${vid}/import/parovani`, { radky: rows, dodavatel: puvod?.dodavatel || "", ceny })
      .then(({ data }) => { if (!zruseno) setPar(Object.fromEntries(data.radky.map((x) => [x.i, x]))); })
      .catch(() => { if (!zruseno) setPar({}); })
      .finally(() => { if (!zruseno) setParNacteno(true); });
    return () => { zruseno = true; };
  }, [rowsVerze, ceny]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rozpracovaná kontrola se průběžně ukládá na server — po zavření se dá obnovit
  // a potvrdit i bez znovunahrání souboru (posílají se schválené řádky).
  const stavKonceptu = useCallback(() => ({
    rows, marze, zdroj, volba, doCeniku, vyrobce, druh, potvrzena, ceny, sleva, mode, puvod, stats,
  }), [rows, marze, zdroj, volba, doCeniku, vyrobce, druh, potvrzena, ceny, sleva, mode, puvod, stats]);
  const ulozKoncept = useCallback(async () => {
    try {
      const { data } = await api.put(`/api/nabidky-verze/${vid}/import/koncept`, { soubor, data: stavKonceptu() });
      setUlozeno(data.updated_at || new Date().toISOString());
    } catch { /* koncept je pomůcka — import funguje i bez něj */ }
  }, [vid, soubor, stavKonceptu]);
  useEffect(() => {
    if (step !== 2 || !rows.length) return undefined;
    clearTimeout(ulozTimer.current);
    ulozTimer.current = setTimeout(ulozKoncept, 1200);
    return () => clearTimeout(ulozTimer.current);
  }, [step, ulozKoncept]); // eslint-disable-line react-hooks/exhaustive-deps

  const obnovKoncept = () => {
    const d = koncept.koncept;
    setRows(d.rows); setMarze(d.marze || {}); setZdroj(d.zdroj || {}); setVolba(d.volba || {});
    setDoCeniku(d.doCeniku || {}); setVyrobce(d.vyrobce || {}); setDruh(d.druh || {}); setPotvrzena(d.potvrzena || {});
    setCeny(d.ceny || "NAKUP"); setSleva(d.sleva || ""); setMode(d.mode || "ai"); setPuvod(d.puvod || null);
    setStats(d.stats || null); setSoubor(koncept.soubor || ""); setUlozeno(koncept.updated_at);
    setVybrane({}); setFiltr("vse"); setKoncept(null); setStep(2); setRowsVerze((v) => v + 1);
  };
  const zahoditKoncept = async () => {
    setKoncept(null);
    try { await api.delete(`/api/nabidky-verze/${vid}/import/koncept`); } catch { /* nevadí */ }
  };

  const pollJob = (jobId) => {
    setAiPhase("reading"); setStep(1);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/api/import-jobs/${jobId}`);
        if (data.status === "done") {
          clearInterval(pollRef.current);
          const res = data.result;
          setPuvod({ dodavatel: res.dodavatel, ico: res.dodavatel_ico, jistota: res.ceny_jistota, navrh: res.ceny_navrh });
          setCeny(res.ceny_navrh === "PRODEJ" ? "PRODEJ" : "NAKUP");
          setSleva(res.sleva_kc ? String(res.sleva_kc).replace(".", ",") : "");
          setFromRows(res.radky, { md: "ai" });
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          setAiPhase(null); setErr(data.detail || "Dokument se nepodařilo přečíst.");
        }
      } catch (e) {
        clearInterval(pollRef.current); setAiPhase(null);
        setErr(apiError(e.response?.data?.detail) || "Čtení se nezdařilo.");
      }
    }, 2000);
  };

  const runAnalyza = async (f, sheetName, map) => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (sheetName) fd.append("sheet", sheetName);
      if (map && Object.keys(map).length) fd.append("mapovani", JSON.stringify(map));
      const { data } = await api.post(`/api/nabidky-verze/${vid}/import/analyza`, fd, NOMULTI);
      if (data.rezim === "AI") { pollJob(data.job_id); return; }
      // Excel s hlavičkou „Dodavatel / Odběratel + IČO“ — směr cen jako u PDF.
      setPuvod(data.ceny_navrh ? { dodavatel: data.dodavatel, ico: data.dodavatel_ico, jistota: data.ceny_jistota, navrh: data.ceny_navrh } : null);
      setCeny(data.ceny_navrh === "PRODEJ" ? "PRODEJ" : "NAKUP");
      setAnalyza(data); setMapping(data.navrh_mapovani || {}); setSheet(data.sheet);
      setFromRows(data.radky, { md: "excel", st: data.stats });
    } catch (e) { setErr(apiError(e.response?.data?.detail) || "Soubor se nepodařilo přečíst."); }
    finally { setBusy(false); }
  };

  const analyzeAI = async () => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (sheet) fd.append("sheet", sheet);
      const { data } = await api.post(`/api/nabidky-verze/${vid}/import/ai`, fd, NOMULTI);
      pollJob(data.job_id);
    } catch (e) { setErr(apiError(e.response?.data?.detail) || "AI analýzu se nepodařilo spustit."); }
    finally { setBusy(false); }
  };

  const onPick = (f) => { if (!f) return; setFile(f); setSoubor(f.name); setKoncept(null); setErr(""); runAnalyza(f); };
  const changeSheet = (name) => { setSheet(name); runAnalyza(file, name); };
  const changeMapping = (field, val) => {
    const m = { ...mapping, [field]: val === "" ? undefined : Number(val) };
    setMapping(m); runAnalyza(file, sheet, m);
  };
  const nastav = (setter, i, hodnota) => setter((o) => ({ ...o, [i]: hodnota }));
  const setRow = (i, patch) => setRows((rs) => rs.map((r) => (r._i === i ? { ...r, ...patch } : r)));

  // --- odvozené hodnoty řádku ---
  const potvrzenaPodobna = (r) => (potvrzena[r._i] && par[r._i]?.podobna?.id === potvrzena[r._i] ? par[r._i].podobna : null);
  const polozka = (r) => par[r._i]?.polozka || potvrzenaPodobna(r);
  const jeNova = (r) => par[r._i]?.match === "NONE" && !potvrzenaPodobna(r);
  const liseSe = (r) => { const x = par[r._i]; return !!(x?.polozka && Math.abs(x.rozdil_kc) >= 0.01); };
  const volbaPro = (r) => volba[r._i] || "NABIDKA";
  const vychoziMarze = (r) => {
    const p = polozka(r);
    if (p && Number(p.vychozi_marze_pct) > 0) {
      const presna = Number(p.vychozi_marze_pct);   // v ceníku může být přesná marže z dřívějšího importu
      return { m: Math.round(presna * 100) / 100, presna, zdroj: "z ceníku" };
    }
    if (Number(r.cena_nakup) > 0 && Number(r.cena_prodej) > Number(r.cena_nakup)) {
      // Přesná marže, ať prodej vyjde na korunu jako ceníková cena dodavatele
      // (1 883 Kč × 42,9 % = 2 691 Kč, ale ceník dodavatele říká 2 690 Kč).
      const presna = (r.cena_prodej / r.cena_nakup - 1) * 100;
      return { m: Math.round(presna * 100) / 100, presna, zdroj: "z ceníkové ceny dodavatele" };
    }
    return { m: null, zdroj: "doplnit" };
  };
  const marzeText = (r) => (marze[r._i] !== undefined ? marze[r._i]
    : (vychoziMarze(r).m == null ? "" : String(vychoziMarze(r).m).replace(".", ",")));
  // Marže pro výpočet a import: ručně zadaná, jinak přesná výchozí (ne zaokrouhlená z políčka).
  const marzePro = (r) => (marze[r._i] !== undefined ? cislo(marze[r._i])
    : (vychoziMarze(r).presna ?? vychoziMarze(r).m));
  // Z prodejních cen (moje nabídka klientovi) se do ceníku ukládá jen na požádání — s prodejní cenou, bez nákupu.
  const doCenikuPro = (r) => (doCeniku[r._i] !== undefined ? doCeniku[r._i] : nakupni && r.typ === "MATERIAL");
  const vyrobcePro = (r) => (vyrobce[r._i] !== undefined ? vyrobce[r._i] : (par[r._i]?.vyrobce_navrh || ""));
  // Shoda jen podle názvu: do ceníku nezakládat (vznikla by druhá položka se stejným názvem).
  const ukazNovou = (r) => !bezCeniku && jeNova(r) && r.typ !== "OSTATNI" && par[r._i]?.podobna?.duvod !== "NAZEV";
  const vypocet = (r) => {
    const mn = Number(r.mnozstvi) || 0;
    if (nakupni) {
      const p = polozka(r);
      const nakup = volbaPro(r) === "CENIK_POUZIT" && p && liseSe(r) ? Number(p.cena_nakup) : Number(r.cena_nakup) || 0;
      const m = marzePro(r);
      return { nakup, m, prodej: nakup * (1 + (m || 0) / 100), mn };
    }
    const prodej = Number(r.cena_nakup) || 0;
    const p = polozka(r);
    const nakup = bezCeniku ? null : nakupy[r._i] !== undefined ? cislo(nakupy[r._i])
      : p && p.cena_nakup != null ? Number(p.cena_nakup) : null;
    // mp = marže z prodeje (zisk ÷ prodej) — tak ji ukazuje řádek i souhrn dole (16 400 / 11 972 → 27 %).
    return { nakup, m: nakup ? (prodej / nakup - 1) * 100 : null, mp: nakup && prodej ? (prodej - nakup) / prodej * 100 : null,
      prodej, mn };
  };
  const bezMarze = (r) => { const m = vypocet(r).m; return m == null || m <= 0; };

  const filtry = [
    ["vse", "Vše", () => true],
    ["nove", "Nové položky", jeNova],
    ["lisi", "Jiná cena než ceník", liseSe],
    ["bez", "Bez marže", bezMarze],
  ].filter(([k]) => !bezCeniku || k === "vse");
  const filtrFn = (filtry.find(([k]) => k === filtr) || filtry[0])[2];
  const viditelne = rows.filter(filtrFn);
  const vybraneIds = Object.keys(vybrane).filter((k) => vybrane[k]).map(Number);
  const hromadne = (setter, hodnota, zdrojText) => {
    setter((o) => { const n = { ...o }; vybraneIds.forEach((i) => { n[i] = hodnota; }); return n; });
    if (zdrojText) setZdroj((o) => { const n = { ...o }; vybraneIds.forEach((i) => { n[i] = zdrojText; }); return n; });
  };
  const hromadneRow = (patch) => setRows((rs) => rs.map((r) => (vybraneIds.includes(r._i) ? { ...r, ...patch } : r)));

  const souhrn = rows.reduce((a, r) => {
    const v = vypocet(r);
    a.nakup += (v.nakup || 0) * v.mn; a.prodej += v.prodej * v.mn;
    return a;
  }, { nakup: 0, prodej: 0 });
  souhrn.zisk = souhrn.prodej - souhrn.nakup;
  const pocetBezMarze = rows.filter(bezMarze).length;
  const noveDoCeniku = rows.filter((r) => ukazNovou(r) && doCenikuPro(r));
  // Prodejní import: napsaný nákup jiný než v ceníku se v ceníku opraví (materiál a práce).
  const opravy = rows.filter((r) => (nakupni ? liseSe(r) && volbaPro(r) === "CENIK_OPRAVIT"
    : !bezCeniku && nakupy[r._i] !== undefined && polozka(r) && r.typ !== "OSTATNI"
      && cislo(nakupy[r._i]) != null && cislo(nakupy[r._i]) !== (polozka(r).cena_nakup == null ? null : Number(polozka(r).cena_nakup))));
  const etapySouhrn = [1, 2, 3].map((e) => ({ e, kc: rows.filter((r) => Number(r.etapa) === e)
    .reduce((a, r) => a + vypocet(r).prodej * vypocet(r).mn, 0) })).filter((x) => x.kc > 0);
  const nejistych = rows.filter((r) => r.typ_jisty === false).length;

  const puvodText = () => {
    if (!puvod) return null;
    const ico = puvod.ico ? ` (IČO ${puvod.ico})` : "";
    if (puvod.jistota === "JISTE" && puvod.navrh === "PRODEJ") return { ok: true, t: <>Dokument vystavila vaše firma{ico} — proto prodejní ceny.</> };
    if (puvod.jistota === "JISTE") return { ok: true, t: <>Dokument vystavil <b>{puvod.dodavatel || "dodavatel"}</b>{ico}{company?.name ? `, ne ${company.name}` : ""} — proto nákupní ceny.</> };
    if (puvod.jistota === "NAVRH") return { ok: false, t: <>Vystavitel {puvod.dodavatel ? <b>{puvod.dodavatel}</b> : null}{ico} — podle názvu jsou ceny {puvod.navrh === "PRODEJ" ? "prodejní" : "nákupní"}. Zkontroluj.</> };
    return { ok: false, t: <>Nepoznali jsme, kdo dokument vystavil — zkontroluj, jestli jsou ceny nákupní.</> };
  };

  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (sheet && file) fd.append("sheet", sheet);
      fd.append("radky", JSON.stringify(rows.map((r) => {
        const nova = ukazNovou(r);
        return {
          ...r, cena_volba: volbaPro(r), marze_pct: nakupni ? marzePro(r) : null,
          do_ceniku: nova && doCenikuPro(r), vyrobce_id: nova && doCenikuPro(r) ? (vyrobcePro(r) || null) : null,
          vyznam: druh[r._i] || "B", polozka_id: potvrzenaPodobna(r)?.id || null,
          nakup: !nakupni && nakupy[r._i] !== undefined ? cislo(nakupy[r._i]) : null,
        };
      })));
      fd.append("ceny", nakupni || bezCeniku ? "NAKUP" : "PRODEJ");
      if (parseKc(sleva) > 0) fd.append("sleva_kc", parseKc(sleva));
      const { data } = await api.post(`/api/nabidky-verze/${vid}/import/potvrzeni`, fd, NOMULTI);
      setSouhrnImportu({ ...souhrn, bezMarze: pocetBezMarze });
      setVysledek(data); setVraceno(null); setStep(3); onDone?.();
      if (data.navrh_nazvu) setNazev(data.navrh_nazvu);
    } catch (e) { setErr(apiError(e.response?.data?.detail) || "Import se nezdařil."); }
    finally { setBusy(false); }
  };

  // Název nabídky (po importu navržený z hlavního zařízení) se uloží při opuštění pole.
  const ulozNazev = async () => {
    const n = nazev.trim();
    if (!nabidka || !n || n === nabidka.nazev) return;
    try { await api.patch(`/api/nabidky/${nabidka.id}`, { nazev: n }); onNazev?.(n); }
    catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
  };

  // Vrátí celý import (řádky, nové položky ceníku, přepsané ceny). Koncept se
  // znovu uloží, ať jde kontrolu upravit a importovat znovu.
  const vratitImport = async () => {
    if (!window.confirm(`Vrátit import?\n\nZ nabídky se odebere ${vysledek.pridano} importovaných položek. `
      + "Položky, které import založil do ceníku, se smažou a přepsané ceny v ceníku se vrátí.")) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/api/import-davky/${vysledek.davka_id}/vratit`);
      setVraceno(data); onDone?.(); ulozKoncept();
    } catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const cols = analyza?.columns || [];
  const jistotaNizka = mode === "excel" && analyza?.mapovani_jistota === "NIZKA";
  const vybranoPocet = vybraneIds.length;
  const pt = puvodText();
  const sazba = dph == null ? null : `${dph} %`;
  const cenikText = bezCeniku ? "" : `${noveDoCeniku.length ? `${noveDoCeniku.length} nových položek do ceníku` : "žádná nová položka do ceníku"} · ${
    opravy.length ? `${opravy.length} ${opravy.length === 1 ? "oprava" : "opravy"} ceny v ceníku` : "ceny v ceníku beze změny"}`;
  const zmenyCeniku = vysledek?.zmeny_ceniku || { nove: [], ceny: [] };

  return (
    <div className="fixed inset-0 z-[70] flex md:items-start justify-center md:p-6 md:pt-8" data-testid="import-wizard">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <section role="dialog" aria-label="Import nabídky"
        className={`relative bg-white w-full flex flex-col shadow-panel md:rounded-card md:max-h-[calc(100vh-56px)] h-full md:h-auto ${step === 2 ? "md:max-w-[1140px]" : "md:max-w-[820px]"}`}>
        <div className="border-b border-line-soft px-4 md:px-[22px] py-3 md:py-4 flex items-center gap-3">
          <div className="min-w-0 flex-1 md:flex-none flex flex-col md:flex-row md:items-center md:gap-3">
            <h2 className="text-[17px] md:text-[16px] font-800">Import nabídky</h2>
            <span className="t-meta text-sec-3">
              Krok {step} ze 3{step === 2 ? " · Kontrola" : step === 3 ? " · Hotovo" : ""}
              {step === 2 && ulozeno ? <span className="md:hidden"> · koncept uložen</span> : null}
            </span>
          </div>
          {soubor && step > 1 && (
            <span className="hidden md:inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-pill bg-surface text-sec t-meta max-w-[320px]" data-testid="import-soubor">
              <FileText size={13} className="shrink-0" /><span className="truncate">{soubor}{mode === "ai" ? " · přečteno AI" : ""}</span>
            </span>
          )}
          {step === 2 && ulozeno && (
            <span className="hidden md:inline-flex ml-auto items-center gap-1.5 text-success t-meta font-600" data-testid="import-koncept-ulozen">
              <Check size={14} strokeWidth={2.4} />Koncept uložen {cas(ulozeno)} — můžeš odejít a pokračovat později
            </span>
          )}
          <button onClick={onClose} className={`tap md:min-h-0 md:min-w-0 h-9 w-9 rounded-full inline-flex items-center justify-center text-ink hover:bg-inputbg ${step === 2 && ulozeno ? "md:ml-0" : "ml-auto"}`}
            aria-label="Zavřít import" data-testid="import-close"><X size={19} /></button>
        </div>

        {err && <div className="mx-4 md:mx-[22px] mt-3 rounded-input bg-[#fdecea] text-danger t-meta px-3 py-2" data-testid="import-error">{err}</div>}

        {step === 1 && (
          <div className="overflow-y-auto flex-1 p-4 md:p-[22px]">
            {aiPhase === "reading" ? (
              <div className="py-12 flex flex-col items-center gap-3 text-center" data-testid="ai-processing">
                <div className="relative"><Sparkles size={40} className="text-accent" />
                  <Loader2 size={20} className="text-accent animate-spin absolute -right-2 -bottom-1" /></div>
                <div className="t-telo font-700 text-ink">Čtu dokument…</div>
                <div className="t-meta text-muted max-w-[360px]">AI prochází nabídku a vytahuje položky. U PDF to obvykle trvá 10–40 sekund.</div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {koncept && (
                  <div className="rounded-card border-[1.5px] border-ink bg-inputbg p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="import-koncept">
                    <div className="min-w-0 flex-1">
                      <div className="t-telo font-700 text-ink">Máš rozpracovaný import</div>
                      <div className="t-meta text-sec-3 truncate">{koncept.soubor || "soubor"} · {koncept.koncept.rows.length} položek · uloženo {cas(koncept.updated_at)}</div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={zahoditKoncept} className="btn-ghost tap md:min-h-0 flex-1 md:flex-none justify-center" data-testid="import-koncept-zahodit">Zahodit</button>
                      <Button onClick={obnovKoncept} className="tap md:min-h-0 flex-1 md:flex-none justify-center" data-testid="import-koncept-pokracovat">Pokračovat</Button>
                    </div>
                  </div>
                )}
                <button onClick={() => fileRef.current?.click()} data-testid="import-dropzone"
                  className="w-full border-2 border-dashed border-line-strong rounded-card py-10 flex flex-col items-center gap-2 hover:border-accent hover:bg-inputbg transition-colors">
                  <UploadCloud size={34} className="text-muted" />
                  <span className="t-telo font-600 text-ink">{koncept ? "Nebo nahraj nový soubor" : "Klikni a vyber soubor"}</span>
                  <span className="t-meta text-muted">PDF, Excel nebo CSV (max 10 MB). PDF přečte AI.</span>
                </button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden"
                  onChange={(e) => onPick(e.target.files?.[0])} data-testid="import-file-input" />
                <p className="t-meta text-muted">V dalším kroku zkontroluješ ceny proti ceníku a nastavíš marži u každé položky.</p>
                {busy && <div className="flex justify-center py-4"><Spinner /></div>}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <>
            <div className="overflow-y-auto flex-1 bg-surface md:bg-white">
              <div className="px-4 md:px-[22px] pt-3 md:pt-4 flex flex-col gap-3 md:gap-3.5">
                {!bezCeniku && (
                  <div className="flex flex-col md:flex-row md:items-stretch gap-2 md:gap-2.5" data-testid="import-ceny">
                    <span className="label-up text-sec-3 md:self-center md:pr-1.5">Ceny v souboru jsou</span>
                    <div className="flex gap-2 md:gap-2.5 flex-1">
                      {[["NAKUP", "Nákupní — od dodavatele", "Prodej = nákup + marže položky."],
                        ["PRODEJ", "Prodejní — moje nabídka klientovi", "Prodej přesně podle souboru, nákup z ceníku nebo ho dopíšeš."]].map(([v, t, d]) => (
                        <button key={v} type="button" onClick={() => setCeny(v)} data-testid={`import-ceny-${v}`}
                          className={`flex-1 text-left rounded-[12px] border-[1.5px] px-3.5 py-2.5 flex flex-col gap-0.5 ${ceny === v ? "border-ink bg-inputbg" : "border-line-strong bg-white"}`}>
                          <span className="t-telo font-700 text-ink">{t}</span>
                          <span className="t-meta text-sec-2">{d}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {pt && (
                  <div className="flex items-start md:items-center gap-2 t-meta text-sec" data-testid="import-puvod">
                    <span className={`h-[7px] w-[7px] rounded-full shrink-0 mt-1.5 md:mt-0 ${pt.ok ? "bg-success" : "bg-accent"}`} />
                    <span>{pt.t}</span>
                  </div>
                )}

                {mode === "excel" && file && (
                  <div className="flex items-center gap-3 flex-wrap">
                    {analyza?.sheets?.length > 1 && (
                      <label className="flex items-center gap-2"><span className="label-up text-muted">List:</span>
                        <Vyber className="field max-w-[220px]" value={sheet || ""} onChange={(e) => changeSheet(e.target.value)} data-testid="import-sheet">
                          {analyza.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Vyber></label>
                    )}
                    <button onClick={() => setShowMapping((v) => !v)} className="btn-ghost h-8 px-2.5 t-meta" data-testid="import-mapping-toggle">
                      <Sliders size={14} /> Mapování sloupců
                    </button>
                    {jistotaNizka && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="pill px-2.5 h-7 inline-flex items-center t-meta font-600 bg-[#fff4e6] text-ink border border-accent/40" data-testid="jistota-nizka">Sloupce se nepodařilo rozpoznat</span>
                        <button onClick={analyzeAI} disabled={busy} className="btn-primary h-8 px-3 t-meta" data-testid="import-ai-btn">
                          <Sparkles size={14} /> Analyzovat pomocí AI
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {showMapping && mode === "excel" && file && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-card bg-inputbg p-3">
                    {FIELDS.map(([f, label]) => (
                      <label key={f} className="flex items-center gap-2">
                        <span className="t-meta w-[120px] shrink-0">{label}</span>
                        <Vyber className="field" value={mapping[f] ?? ""} data-testid={`map-${f}`} onChange={(e) => changeMapping(f, e.target.value)}>
                          <option value="">— nemapovat —</option>
                          {cols.map((c, i) => <option key={i} value={i}>{c}</option>)}
                        </Vyber>
                      </label>
                    ))}
                  </div>
                )}

                {(
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 t-meta text-sec-3" data-testid="import-nalezeno">
                    {etapySouhrn.length > 0 && <span>Etapy: {etapySouhrn.map((x) => <b key={x.e} className="text-ink mr-2">{ETAPA_RIM[x.e]}. {fmtKc(x.kc)} Kč</b>)}</span>}
                    {nejistych > 0 && <span className="text-accent font-600" data-testid="import-nejiste">{nejistych} položek s odhadnutým typem — zkontroluj</span>}
                    {stats?.preskoceno_nulove > 0 && <span data-testid="import-stats">{stats.preskoceno_nulove} řádků s množstvím 0 přeskočeno</span>}
                    <label className="flex items-center gap-2 md:ml-auto">
                      <span>Sleva pod čarou</span>
                      <input type="text" inputMode="decimal" value={sleva} placeholder="0" data-testid="import-sleva"
                        onChange={(e) => setSleva(e.target.value.replace(/[^\d.,\s]/g, ""))}
                        className="field amount !h-9 w-[110px] text-right" />
                      <span>Kč</span>
                    </label>
                  </div>
                )}

                <div className="flex gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:items-center pb-0.5">
                  {filtry.map(([k, label, fn]) => (
                    <button key={k} type="button" onClick={() => setFiltr(k)} data-testid={`import-filtr-${k}`}
                      className={`${FILTR} ${filtr === k ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong"}`}>
                      {label}<span className="amount opacity-60 font-700">{rows.filter(fn).length}</span>
                    </button>
                  ))}
                  <span className="hidden md:inline ml-auto t-meta text-sec-3">Zaškrtni řádky a nastav jim marži, druh nebo etapu najednou.</span>
                </div>
                <div className="md:hidden flex items-center justify-between">
                  <span className="t-meta text-sec-3">{viditelne.length} položek</span>
                  <button type="button" onClick={() => { setVybirani((v) => !v); setVybrane({}); }} className="btn-ghost tap" data-testid="import-vybirat">
                    {vybirani ? "Hotovo" : "Vybrat"}
                  </button>
                </div>

                {vybranoPocet > 0 && (
                  <div className="hidden md:flex flex-wrap items-center gap-x-3.5 gap-y-2 px-3.5 py-2.5 rounded-[12px] bg-ink text-white" data-testid="import-hromadne">
                    <span className="font-700">Vybráno {vybranoPocet}</span>
                    <HromadneAkce nakupni={nakupni} bezCeniku={bezCeniku} onMarze={(m) => hromadne(setMarze, String(m), "hromadně")}
                      onEtapa={(e) => hromadneRow({ etapa: e })} onTyp={(t) => hromadneRow({ typ: t, typ_jisty: true })}
                      vyrobci={vyrobci} onVyrobce={(x) => hromadne(setVyrobce, x)} />
                    <button type="button" className={`${CHIP} ml-auto border-transparent`} onClick={() => setVybrane({})}>Zrušit výběr</button>
                  </div>
                )}
              </div>

              <div className="px-4 md:px-[22px] pt-3 pb-4">
                <div className="md:border md:border-line md:rounded-[12px] md:overflow-hidden flex flex-col gap-2.5 md:gap-0">
                  <div className="hidden md:grid grid-cols-[30px_minmax(0,1fr)_70px_112px_96px_112px_120px_64px] gap-2.5 px-3.5 py-2 bg-inputbg border-b border-line label-up text-sec-3">
                    <span /><span>Položka</span><span className="text-right">Množ.</span>
                    <span className={`text-right ${nakupni ? "text-ink" : ""}`}>{bezCeniku ? "" : "Nákup / ks"}</span>
                    <span className="text-right">{bezCeniku ? "" : "Marže"}</span>
                    <span className={`text-right ${nakupni ? "" : "text-ink"}`}>{bezCeniku ? "Cena / ks" : "Prodej / ks"}</span>
                    <span className="text-right">{bezCeniku ? "Celkem" : "Prodej celkem"}</span><span>Etapa</span>
                  </div>
                  {rows.length === 0 && <div className="px-3 py-4 t-meta text-muted">Žádné řádky. Zkontroluj mapování sloupců (Název + Cena).</div>}
                  {viditelne.map((r) => (
                    <Radek key={r._i} r={r} v={vypocet(r)} nakupni={nakupni} bezCeniku={bezCeniku}
                      par={par[r._i]} nova={jeNova(r)} lisi={liseSe(r)} volba={volbaPro(r)}
                      marze={marzeText(r)} zdroj={marze[r._i] !== undefined ? (zdroj[r._i] || "ručně") : vychoziMarze(r).zdroj}
                      bezMarze={bezMarze(r)} ukazNovou={ukazNovou(r)} doCeniku={doCenikuPro(r)} vyrobce={vyrobcePro(r)} druh={druh[r._i] || "B"}
                      potvrzena={!!potvrzenaPodobna(r)} polozkaCeniku={polozka(r)}
                      onPotvrdit={() => nastav(setPotvrzena, r._i, potvrzenaPodobna(r) ? null : par[r._i]?.podobna?.id)}
                      vyrobci={vyrobci} vybrano={!!vybrane[r._i]} vybirani={vybirani}
                      onVyber={() => nastav(setVybrane, r._i, !vybrane[r._i])}
                      onMarze={(t) => { nastav(setMarze, r._i, t.replace(/[^\d.,-]/g, "")); nastav(setZdroj, r._i, "ručně"); }}
                      nakupText={nakupy[r._i] !== undefined ? nakupy[r._i] : (polozka(r)?.cena_nakup != null ? String(polozka(r).cena_nakup).replace(".", ",") : "")}
                      nakupRucne={nakupy[r._i] !== undefined} onNakup={(t) => nastav(setNakupy, r._i, t.replace(/[^\d.,]/g, ""))}
                      onVolba={(o) => nastav(setVolba, r._i, o)} onDoCeniku={() => nastav(setDoCeniku, r._i, !doCenikuPro(r))}
                      onVyrobce={(x) => nastav(setVyrobce, r._i, x)} onDruh={(k) => nastav(setDruh, r._i, k)}
                      onEtapa={(e) => setRow(r._i, { etapa: e })} onTyp={(t) => setRow(r._i, { typ: t, typ_jisty: true })} />
                  ))}
                </div>
              </div>

            </div>
            <div className="hidden md:flex mx-[22px] mt-3 flex-wrap gap-x-7 gap-y-2.5 items-end px-[18px] py-3.5 rounded-[14px] bg-ink text-white" data-testid="import-souhrn">
              {!bezCeniku && <Cislo popisek="Nákup celkem" hodnota={`${fmtKc(souhrn.nakup)} Kč`} />}
              <Cislo popisek="Prodej celkem bez DPH" hodnota={`${fmtKc(souhrn.prodej)} Kč`} />
              {!bezCeniku && <Cislo popisek="Marže" barva="#5fd3a5" hodnota={souhrn.prodej ? `${pct(souhrn.zisk / souhrn.prodej * 100)} · zisk ${fmtKc(souhrn.zisk)} Kč` : "—"} />}
              <div className="ml-auto text-right text-[#c9ced5] t-meta flex flex-col gap-0.5">
                {cenikText && <span>{cenikText}</span>}
                <span>DPH se vezme z nabídky{sazba ? ` (teď ${sazba})` : ""}.{parseKc(sleva) > 0 ? ` Sleva pod čarou ${fmtKc(parseKc(sleva))} Kč.` : ""}</span>
              </div>
            </div>

            {/* Telefon: dole buď hromadné akce, nebo souhrn s tlačítkem importu. */}
            {vybirani && vybranoPocet > 0 ? (
              <div className="md:hidden bg-ink text-white px-4 pt-3 pb-[max(18px,env(safe-area-inset-bottom))] flex flex-col gap-2.5" data-testid="import-hromadne-mobil">
                <div className="flex items-center"><span className="font-700 flex-1">Vybráno {vybranoPocet}</span>
                  <button type="button" className={`${CHIP} border-transparent`} onClick={() => setVybrane({})}>Zrušit</button></div>
                <div className="flex flex-wrap gap-1.5">
                  <HromadneAkce nakupni={nakupni} bezCeniku={bezCeniku} onMarze={(m) => hromadne(setMarze, String(m), "hromadně")}
                    onEtapa={(e) => hromadneRow({ etapa: e })} onTyp={(t) => hromadneRow({ typ: t, typ_jisty: true })}
                    vyrobci={vyrobci} onVyrobce={(x) => hromadne(setVyrobce, x)} />
                </div>
              </div>
            ) : (
              <div className="md:hidden bg-white border-t border-line px-4 pt-2.5 pb-[max(18px,env(safe-area-inset-bottom))] flex flex-col gap-2">
                <div className="flex justify-between t-meta">
                  <span className="text-sec-3">Prodej <b className="amount text-ink">{fmtKc(souhrn.prodej)} Kč</b></span>
                  {!bezCeniku && souhrn.prodej > 0 && <span className="amount text-success font-700">{pct(souhrn.zisk / souhrn.prodej * 100)}</span>}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setStep(1); setAnalyza(null); setAiPhase(null); }} className="btn-ghost tap justify-center" aria-label="Zpět"><ArrowLeft size={16} /></button>
                  <Button onClick={confirm} disabled={busy || rows.length === 0 || !parNacteno} className="tap flex-1 justify-center" data-testid="import-confirm-mobil">
                    {busy ? <Spinner /> : `Importovat ${rows.length} položek`}</Button>
                </div>
              </div>
            )}
            <div className="hidden md:flex items-center gap-2.5 px-[22px] pt-3.5 pb-[18px] border-t border-line-soft">
              <button type="button" onClick={() => { setStep(1); setAnalyza(null); setAiPhase(null); }} className="btn-ghost h-10" data-testid="import-back"><ArrowLeft size={15} /> Zpět</button>
              <span className="ml-auto t-meta text-sec-3" data-testid="import-poznamka">
                {!nakupni ? "" : pocetBezMarze ? `${pocetBezMarze} položek zatím bez marže — doplníš i později v nabídce` : "Všechny položky mají marži"}
              </span>
              <Button onClick={confirm} disabled={busy || rows.length === 0 || !parNacteno} className="h-10" data-testid="import-confirm">
                {busy ? <Spinner /> : `Importovat ${rows.length} položek`}</Button>
            </div>
          </>
        )}

        {step === 3 && vysledek && (
          <div className="overflow-y-auto flex-1 bg-surface md:bg-white" data-testid="import-result">
            {!vraceno ? (
              <div className="p-4 md:p-[22px] flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                  <span className="hidden md:inline-flex h-[34px] w-[34px] rounded-full bg-success-bg text-success items-center justify-center"><CheckCircle2 size={18} /></span>
                  <h3 className="text-[20px] md:text-[18px] font-800">Přidáno {vysledek.pridano} položek do nabídky</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3">
                  <Dlazdice popisek="Prodej bez DPH" hodnota={`${fmtKc(souhrnImportu?.prodej || 0)} Kč`} />
                  {!bezCeniku && <Dlazdice popisek="Marže" barva="#5fd3a5" hodnota={souhrnImportu?.prodej ? pct(souhrnImportu.zisk / souhrnImportu.prodej * 100) : "—"} />}
                  {!bezCeniku && vysledek.ceny !== "PRODEJ" && (
                    <Dlazdice popisek="Bez marže" hodnota={souhrnImportu?.bezMarze ? `${souhrnImportu.bezMarze} položek` : "žádná"} className="hidden md:block" />
                  )}
                </div>
                {vysledek.ceny === "PRODEJ" && <p className="t-meta text-sec" data-testid="import-vysledek-prodej">Ceny pro klienta zůstaly přesně podle souboru{vysledek.zalozeno_do_ceniku
                  ? `, do ceníku přibylo ${vysledek.zalozeno_do_ceniku} ${vysledek.zalozeno_do_ceniku === 1 ? "položka" : vysledek.zalozeno_do_ceniku < 5 ? "položky" : "položek"} s prodejní cenou (nákup doplníš později).`
                  : ", ceník se nezměnil."}</p>}
                {nabidka && (
                  <label className="flex flex-col gap-1" data-testid="import-nazev">
                    <span className="label-up text-sec-3">Název nabídky</span>
                    <input className="field" value={nazev} onChange={(e) => setNazev(e.target.value)} onBlur={ulozNazev}
                      placeholder="např. Zehnder Q350 + ComfoClime" data-testid="import-nazev-input" />
                    {vysledek.navrh_nazvu && nazev === vysledek.navrh_nazvu && (
                      <span className="t-meta text-sec-3">Navrženo podle hlavního zařízení — uprav, jak potřebuješ.</span>
                    )}
                  </label>
                )}
                {!bezCeniku && (
                  <p className="t-meta text-sec" data-testid="import-zmeny-ceniku">
                    {zmenyCeniku.nove.length || zmenyCeniku.ceny.length
                      ? `Ceník: ${zmenyCeniku.nove.length ? `${zmenyCeniku.nove.length} nových položek` : ""}${zmenyCeniku.nove.length && zmenyCeniku.ceny.length ? " · " : ""}${zmenyCeniku.ceny.length ? `${zmenyCeniku.ceny.length} oprav ceny` : ""}.`
                      : "Ceník zůstal beze změny."}
                  </p>
                )}
                <PrehledImportu radky={vysledek.radky || []} bezCeniku={bezCeniku} />
                <div className="flex flex-col md:flex-row md:items-center gap-2.5">
                  {vysledek.davka_id && (
                    <button type="button" onClick={vratitImport} disabled={busy} className="btn-ghost tap md:min-h-0 h-10 justify-center" data-testid="import-vratit">
                      <RotateCcw size={15} /> Vrátit import
                    </button>
                  )}
                  <span className="hidden md:inline t-meta text-sec-3">Import jde vrátit i později: v nabídce ⋯ → Historie importů.</span>
                  <Button onClick={async () => { await ulozNazev(); onDone?.(); onClose(); }} className="tap md:min-h-0 h-10 md:ml-auto justify-center" data-testid="import-finish">Hotovo</Button>
                </div>
              </div>
            ) : (
              <div className="px-4 md:px-[22px] py-12 flex flex-col items-center gap-2.5 text-center" data-testid="import-vraceno">
                <h3 className="text-[20px] md:text-[18px] font-800">Import vrácen</h3>
                <p className="t-telo text-sec-3 max-w-[460px]">Nabídka i ceník jsou jako před importem. Koncept importu zůstal — můžeš ho upravit a importovat znovu.</p>
                {vraceno.ponechano?.length > 0 && <p className="t-meta text-[#8a5300] max-w-[460px]">Ponecháno: {vraceno.ponechano.join("; ")}.</p>}
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => { setStep(2); setVysledek(null); setVraceno(null); }} className="btn-ghost tap md:min-h-0 h-10" data-testid="import-zpet-kontrola">Zpět na kontrolu</button>
                  <Button onClick={onClose} className="tap md:min-h-0 h-10" data-testid="import-zavrit">Zavřít</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

// Druh (hlavní zařízení…) se nastavuje u každé nové položky zvlášť — hromadně ho nechceme.
function HromadneAkce({ nakupni, bezCeniku, onMarze, onEtapa, onTyp, vyrobci = [], onVyrobce }) {
  return (
    <>
      {/* Výrobce označeným novým položkám (např. celá konzole AIT najednou) — platí pro ty, co jdou do ceníku. */}
      {!bezCeniku && vyrobci.length > 0 && (
        <label className="inline-flex items-center gap-1.5"><span className="label-up text-ondark-muted">Výrobce</span>
          <select defaultValue="" onChange={(e) => { if (e.target.value !== "") { onVyrobce(e.target.value === "-" ? "" : e.target.value); e.target.value = ""; } }}
            aria-label="Výrobce označených položek" data-testid="bulk-vyrobce"
            className="h-10 md:h-8 rounded-pill bg-white/10 text-white border border-white/25 px-3 text-[16px] md:text-[12px] font-600 outline-none max-w-[180px]">
            <option value="" className="text-ink">Vybrat…</option>
            <option value="-" className="text-ink">bez výrobce</option>
            {vyrobci.map((x) => <option key={x.id} value={x.id} className="text-ink">{x.name}</option>)}
          </select>
        </label>
      )}
      {nakupni && (
        <span className="inline-flex items-center gap-1.5"><span className="label-up text-ondark-muted">Marže</span>
          {[25, 30, 35].map((m) => <button key={m} type="button" className={CHIP} onClick={() => onMarze(m)} data-testid={`bulk-marze-${m}`}>{m} %</button>)}
        </span>
      )}
      <span className="inline-flex items-center gap-1.5"><span className="label-up text-ondark-muted">Etapa</span>
        {[1, 2, 3, 0].map((e) => <button key={e} type="button" className={`${CHIP} min-w-[34px]`} onClick={() => onEtapa(e)} data-testid={`bulk-etapa-${e}`}>{ETAPA_RIM[e]}</button>)}
      </span>
      <span className="inline-flex items-center gap-1.5"><span className="label-up text-ondark-muted">Typ</span>
        {["MATERIAL", "PRACE", "OSTATNI"].map((t) => <button key={t} type="button" className={CHIP} onClick={() => onTyp(t)} data-testid={`bulk-typ-${t}`}>{TYP_LABEL[t]}</button>)}
      </span>
    </>
  );
}

function Stitek({ nova, lisi, par }) {
  if (!par) return null;
  if (nova) return <span className={`${STITEK} bg-ink text-white`}>Nová</span>;
  if (lisi) return <span className={`${STITEK} bg-[#fdf3e2] text-[#8a5300]`}>Jiná cena</span>;
  return <span className={`${STITEK} bg-success-bg text-success`}>V ceníku</span>;
}

// Jeden řádek kontroly: na počítači řádek tabulky, na telefonu karta.
function Radek({ r, v, nakupni, bezCeniku, par, nova, lisi, volba, marze, zdroj, bezMarze, ukazNovou, doCeniku, vyrobce, druh,
  vyrobci, vybrano, vybirani, onVyber, onMarze, onVolba, onDoCeniku, onVyrobce, onDruh, onEtapa, onTyp,
  potvrzena = false, polozkaCeniku = null, onPotvrdit, nakupText: nakupHodnota = "", nakupRucne = false, onNakup }) {
  const p = polozkaCeniku;
  const rozdil = lisi ? par.rozdil_kc : 0;
  const zdrojText = nakupni ? (bezMarze ? "doplnit" : zdroj) : (v.m == null ? "doplň nákup" : "z prodeje");
  const zdrojStyl = (nakupni && bezMarze) || (!nakupni && v.m == null) ? "text-accent-hover font-600" : "text-sec-3";
  const nakupText = bezCeniku ? "" : v.nakup == null ? "doplnit" : `${fmtKc(v.nakup)} Kč`;
  // Prodejní import: nákup jde napsat přímo k řádku (uloží se i do ceníku).
  const nakupVstup = (cls) => (
    <input className={`field amount text-right ${cls} ${v.nakup == null ? "!border-accent !bg-[#fff7f2]" : ""}`} inputMode="decimal"
      value={nakupHodnota} onChange={(e) => onNakup(e.target.value)} placeholder="doplnit" aria-label="Nákup za kus v Kč" data-testid={`imp-nakup-${r._i}`} />
  );
  const prodejniNakup = !nakupni && !bezCeniku;
  const typSelect = (
    <select value={r.typ} onChange={(e) => onTyp(e.target.value)} data-testid={`imp-typ-${r._i}`} aria-label="Typ položky"
      className={`bg-transparent border-0 p-0 pr-4 t-meta font-600 outline-none ${r.typ_jisty === false ? "text-accent" : "text-sec-3"}`}>
      {Object.entries(TYP_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
  const meta = (
    <span className="flex flex-wrap items-center gap-x-1.5 t-meta text-sec-3 amount">
      {typSelect}
      {r.katalogove_cislo ? <span>· kat. č. {r.katalogove_cislo}</span> : null}
      {/* Na co se řádek napároval — u jiného názvu i název a kat. číslo z ceníku, ať je vidět, odkud je nákup. */}
      {p && !bezCeniku ? (
        <span data-testid={`imp-polozka-${r._i}`}>
          · v ceníku{p.name && p.name !== r.nazev ? ` „${p.name}“` : ""}{p.katalogove_cislo && p.katalogove_cislo !== r.katalogove_cislo ? ` (${p.katalogove_cislo})` : ""}
          {" "}· nákup {p.cena_nakup == null ? "neznámý" : `${fmtKc(p.cena_nakup)} Kč`}
        </span>
      ) : null}
      {r.cena_puvodni ? <span className="text-success">· sleva {r.sleva_pct} % z {fmtKc(r.cena_puvodni)} Kč</span> : null}
      {r.poznamka ? <span className="italic">· {r.poznamka}</span> : null}
    </span>
  );
  const marzeVstup = nakupni ? (
    <span className="inline-flex items-center gap-1">
      <input className={`field amount !h-10 md:!h-[30px] !w-[72px] md:!w-[58px] !px-2 text-right ${bezMarze ? "!border-accent !bg-[#fff7f2]" : ""}`}
        inputMode="decimal" value={marze} onChange={(e) => onMarze(e.target.value)} aria-label="Marže v procentech" data-testid={`imp-marze-${r._i}`} />
      <span className="text-sec-3">%</span>
    </span>
  ) : bezCeniku ? null : (
    <span className={`amount font-600 ${v.mp != null && v.mp < 0 ? "text-danger" : ""}`}
      title={v.m == null ? undefined : `Přirážka k nákupu ${pct(v.m)}`}>{v.mp == null ? "—" : pct(v.mp)}</span>
  );
  const etapaSelect = (cls) => (
    <select className={cls} value={Number(r.etapa) || 0} onChange={(e) => onEtapa(Number(e.target.value))} aria-label="Etapa" data-testid={`imp-etapa-${r._i}`}>
      {[0, 1, 2, 3].map((e) => <option key={e} value={e}>{ETAPA_RIM[e]}</option>)}
    </select>
  );
  const rozdilBlok = nakupni && lisi && (
    <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-1.5 md:gap-x-3 md:gap-y-2" data-testid={`imp-rozdil-${r._i}`}>
      <span className="amount t-meta text-sec">
        Ceník {fmtKc(p.cena_nakup)} Kč → soubor {fmtKc(r.cena_nakup)} Kč{par.rozdil_pct != null ? ` (${rozdil > 0 ? "+" : ""}${String(par.rozdil_pct).replace(".", ",")} %)` : ""}
      </span>
      <span role="group" aria-label="Co s rozdílem ceny" className={SEG_SKUPINA}>
        {VOLBY.map(([k, l]) => <button key={k} type="button" className={segCls(volba === k)} onClick={() => onVolba(k)} data-testid={`imp-volba-${r._i}-${k}`}>{l}</button>)}
      </span>
      <span className="t-meta text-sec-3">{VOLBA_POPIS[volba]}</span>
    </div>
  );
  const novaBlok = ukazNovou && (
    <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-x-3">
      <label className="inline-flex items-center gap-2 t-meta text-sec min-h-[32px] md:min-h-0">
        <input type="checkbox" checked={doCeniku} onChange={onDoCeniku} className="h-5 w-5 md:h-[15px] md:w-[15px] accent-ink" data-testid={`imp-do-ceniku-${r._i}`} />
        {nakupni ? "Založit do ceníku" : v.nakup != null ? "Uložit do ceníku s prodejní cenou a nákupem" : "Uložit do ceníku s prodejní cenou (nákup doplníš později)"}
      </label>
      {doCeniku && r.typ === "MATERIAL" && (
        <>
          <label className="inline-flex items-center gap-1.5 t-meta text-sec">
            <span className="hidden md:inline">Výrobce</span>
            <Vyber className="field md:!h-[30px] md:!w-[160px]" value={vyrobce} onChange={(e) => onVyrobce(e.target.value)} aria-label="Výrobce" data-testid={`imp-vyrobce-${r._i}`}>
              <option value="">bez výrobce</option>
              {vyrobci.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </Vyber>
          </label>
          <span role="group" aria-label="Druh položky" className={SEG_SKUPINA}>
            {["A", "B", "C"].map((k) => <button key={k} type="button" className={segCls(druh === k)} onClick={() => onDruh(k)} data-testid={`imp-druh-${r._i}-${k}`}>{DRUH[k]}</button>)}
          </span>
        </>
      )}
    </div>
  );
  const podobna = par?.podobna;
  const varovani = podobna && (podobna.duvod === "NAZEV" ? (
    // Prodejní import: stejný název bez katalogového čísla může být jiná věc (jiný nákup) — potvrdí uživatel.
    <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-1.5 md:gap-x-3 t-meta" data-testid={`imp-podobna-${r._i}`}>
      <span className={`flex items-center gap-2 ${potvrzena ? "text-sec" : "text-[#9a5b00]"}`}>
        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${potvrzena ? "bg-success" : "bg-[#e0901a]"}`} />
        {potvrzena
          ? <>Spárováno s „{podobna.name}“ — nákup z ceníku se použije.</>
          : <>V ceníku je „{podobna.name}“{podobna.cena_nakup != null ? ` (nákup ${fmtKc(podobna.cena_nakup)} Kč)` : ""} — shoda jen podle názvu, nákup se nebere.</>}
      </span>
      <button type="button" onClick={onPotvrdit} data-testid={`imp-potvrdit-${r._i}`}
        className="tap md:min-h-0 self-start t-meta font-700 text-accent hover:text-accent-hover">
        {potvrzena ? "Zrušit spárování" : "Je to ona — vzít nákup"}
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-2 t-meta text-[#9a5b00]" data-testid={`imp-podobna-${r._i}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-[#e0901a] shrink-0" />
      V ceníku je „{podobna.name}“{podobna.vyrobce ? ` od ${podobna.vyrobce}` : ""} — jiný výrobce, proto nespárováno.
    </div>
  ));
  const pozadi = vybrano ? "bg-surface md:bg-surface" : nova ? "bg-[#fffaf5]" : "bg-white";

  return (
    <div className={`${pozadi} rounded-card md:rounded-none border border-line-softer md:border-0 md:border-b md:border-line-soft md:last:border-b-0`} data-testid={`imp-row-${r._i}`}>
      {/* počítač */}
      <div className="hidden md:grid grid-cols-[30px_minmax(0,1fr)_70px_112px_96px_112px_120px_64px] gap-2.5 px-3.5 pt-2.5 pb-1.5 items-start">
        <input type="checkbox" checked={vybrano} onChange={onVyber} aria-label="Vybrat řádek" className="h-4 w-4 mt-0.5 accent-ink" data-testid={`imp-check-${r._i}`} />
        <span className="min-w-0 flex flex-col gap-1">
          <span className="flex items-center gap-1.5 flex-wrap"><Stitek nova={nova} lisi={lisi} par={par} /><span className="t-telo font-600 text-ink">{r.nazev}</span></span>
          {meta}
        </span>
        <span className="amount text-right pt-0.5 text-sec">{r.mnozstvi} {r.mj}</span>
        {prodejniNakup ? nakupVstup("!h-[30px] !px-2 w-full min-w-0")
          : <span className={`amount text-right pt-0.5 ${v.nakup == null ? "text-accent-hover font-600" : nakupni ? "font-700" : "text-sec"}`}>{nakupText}</span>}
        <span className="flex flex-col items-end gap-0.5">
          {marzeVstup}
          {!bezCeniku && <span className={`t-meta text-right ${zdrojStyl}`}>{zdrojText}</span>}
        </span>
        <span className={`amount text-right pt-0.5 ${nakupni ? "text-sec" : "font-700"}`}>{fmtKc(v.prodej)} Kč</span>
        <span className="amount text-right pt-0.5 font-700">{fmtKc(v.prodej * v.mn)} Kč</span>
        {etapaSelect("field !h-[30px] !w-[60px] !px-1.5 !pr-5")}
      </div>
      {(rozdilBlok || novaBlok || varovani) && (
        <div className="hidden md:flex flex-col gap-2 pl-[54px] pr-3.5 pb-2.5">{rozdilBlok}{novaBlok}{varovani}</div>
      )}

      {/* telefon */}
      <div className="md:hidden p-3.5 flex flex-col gap-2.5">
        <div className="flex gap-2.5 items-start">
          {vybirani && <input type="checkbox" checked={vybrano} onChange={onVyber} aria-label="Vybrat řádek" className="h-[22px] w-[22px] mt-0.5 accent-ink shrink-0" />}
          <span className="min-w-0 flex-1">
            <Stitek nova={nova} lisi={lisi} par={par} />
            <span className="block t-telo font-600 text-ink mt-1">{r.nazev}</span>
            {meta}
          </span>
          {etapaSelect("field !h-10 !w-[64px] !px-2 !pr-6 shrink-0")}
        </div>
        <div className="grid grid-cols-3 gap-2 items-end">
          <div>{!bezCeniku && <><div className="label-up text-sec-3">Nákup / ks</div>{prodejniNakup ? nakupVstup("!h-10 !px-2 w-full min-w-0")
            : <div className={`amount font-700 pb-2 ${v.nakup == null ? "text-accent-hover" : ""}`}>{nakupText}</div>}</>}</div>
          <div>{!bezCeniku && <><div className="label-up text-sec-3">Marže</div>{nakupni ? marzeVstup : <div className="pb-2">{marzeVstup}</div>}</>}</div>
          <div className="text-right"><div className="label-up text-sec-3">{bezCeniku ? "Cena / ks" : "Prodej / ks"}</div><div className="amount font-700 pb-2">{fmtKc(v.prodej)} Kč</div></div>
        </div>
        <div className="flex justify-between t-meta -mt-1.5">
          {!bezCeniku ? <span className={zdrojStyl}>{zdrojText}</span> : <span />}
          <span className="text-sec-3">{r.mnozstvi} {r.mj} · <b className="amount text-ink">{fmtKc(v.prodej * v.mn)} Kč</b></span>
        </div>
        {rozdilBlok && <div className="pt-2 border-t border-line-soft">{rozdilBlok}</div>}
        {novaBlok && <div className="pt-2 border-t border-line-soft">{novaBlok}</div>}
        {varovani}
      </div>
    </div>
  );
}

function Cislo({ popisek, hodnota, barva }) {
  return <div><div className="label-up text-ondark-muted">{popisek}</div><div className="t-cislo-velke !text-[17px] font-700" style={barva ? { color: barva } : undefined}>{hodnota}</div></div>;
}

function Dlazdice({ popisek, hodnota, barva, className = "" }) {
  return (
    <div className={`px-4 py-3.5 rounded-[14px] bg-ink text-white ${className}`}>
      <div className="label-up text-ondark-muted">{popisek}</div>
      <div className="t-cislo-velke !text-[18px] font-700" style={barva ? { color: barva } : undefined}>{hodnota}</div>
    </div>
  );
}
